import { type BaseSyntheticEvent } from 'react';
import type {
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormReturn,
} from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { clsx } from 'clsx';
import { Button } from '@repo/ui/Button';
import { Icon } from '@repo/ui/Icon';
import { Input } from '@repo/ui/Input';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputMessage } from '@repo/ui/Input/InputMessage';
import { InputRoot } from '@repo/ui/Input/InputRoot';
import { rawString, Text } from '../Language/Language';
import { StepHeader } from '../StepHeader/StepHeader';
import { GiftSubSection } from './SubSections';
import { Flex } from '@repo/ui/Flex';
import type { OrderTokenType } from '../../graphql/query/getRetailerSettings';
import { IconButton } from '@repo/ui/IconButton';
import { Popover } from '@repo/ui/Popover';
import { PopoverRoot } from '@repo/ui/Popover/PopoverRoot';
import { PopoverTrigger } from '@repo/ui/Popover/PopoverTrigger';
import { PopoverContentSimple } from '@repo/ui/Popover/PopoverContentSimple';
import s from './Login.module.css';

export interface LoginFormProps<T extends FieldValues = FieldValues> {
  fields: OrderTokenType[];
  errors: FieldErrors<T>;
  register: UseFormRegister<{
    EMAIL: unknown;
    ZIPCODE: unknown;
    ORDER_NUMBER: unknown;
  }>;
  handleSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  isLoading: boolean;
  isValid: boolean;
  isError: boolean;
  isCentered?: boolean;
  methods: UseFormReturn<{
    EMAIL: unknown;
    ZIPCODE: unknown;
    ORDER_NUMBER: unknown;
  }>;
  showGiftSection?: boolean;
  onGiftLoginClick?: () => void;
}

/**
 * Pure presentational LoginForm component.
 * Contains the exact same JSX as the original Login component,
 * but receives all values as props instead of using hooks directly.
 */
export const LoginForm = ({
  fields,
  errors,
  register,
  handleSubmit,
  isLoading,
  isValid,
  isError,
  isCentered,
  methods,
  showGiftSection,
  onGiftLoginClick,
}: LoginFormProps) => {
  return (
    <Flex
      gap="g-24"
      padding="p-32"
      direction="column"
      className={clsx(s.wrapper, isCentered && s.centered)}
    >
      <FormProvider {...methods}>
        <form className={s.form} onSubmit={handleSubmit}>
          <StepHeader
            title={{ langKey: 'Global.login.title' }}
            subtitle={{ langKey: 'Global.login.description' }}
          />

          <div className={s.inputContainer}>
            {fields.map((field) => {
              return (
                <InputRoot key={field} width="full">
                  <InputGroup>
                    <InputField>
                      <InputLabel
                        htmlFor={field}
                        variant={errors[field] ? 'error' : 'default'}
                      >
                        {rawString({ langKey: `Global.login.${field}` })}
                      </InputLabel>
                      <Input id={field} {...register(field)} />
                    </InputField>
                    <PopoverRoot>
                      <PopoverTrigger asChild={true}>
                        <IconButton
                          icon="help-circle"
                          label={rawString({
                            langKey: 'Global.help',
                          })}
                        />
                      </PopoverTrigger>
                      <Popover
                        placement="left"
                        align="center"
                        ariaLabel={rawString({
                          langKey: 'Global.info',
                        })}
                      >
                        <PopoverContentSimple
                          closeButtonLabel={rawString({
                            langKey: 'Global.close',
                          })}
                        >
                          <Text langKey={`Global.login.help.${field}`} />
                        </PopoverContentSimple>
                      </Popover>
                    </PopoverRoot>
                  </InputGroup>
                  {errors[field] && (
                    <InputMessage variant="error">
                      <Icon name="alert" size="font" />
                      {errors[field]?.message as string}
                    </InputMessage>
                  )}
                </InputRoot>
              );
            })}
          </div>
          <Button
            isFullWidth={true}
            disabled={isLoading || !isValid}
            isLoading={isLoading}
            type="submit"
            size="lg"
          >
            <Text langKey="Global.login.submitButton" />
          </Button>
          {isError && (
            <div className={s.error} role="alert">
              <Text langKey="Global.login.submitErrorMessage" />
            </div>
          )}
        </form>

        <div className={s.policy} aria-live="polite">
          <a
            href={rawString({ langKey: 'Global.login.policyUrl' })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Text langKey="Global.login.policyDescription" />
          </a>
        </div>
      </FormProvider>

      {showGiftSection && onGiftLoginClick && (
        <GiftSubSection onChange={onGiftLoginClick} />
      )}
    </Flex>
  );
};
